import { StarterKit } from '@tiptap/starter-kit';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import type { AnyExtension } from '@tiptap/react';
import { ImageNode } from './imageNode';
import { MentionNode } from './mentionNode';
import { lowlight } from './highlighting';

/**
 * The one node set, shared by every editor in the app.
 *
 * There are two now — the message composer and the request description — and
 * the whole reason this list is a module rather than an inline array is that a
 * second copy of it would drift. It is hand-matched to the server's allowlist
 * in `apps/api/src/modules/messages/message-body.ts`: anything enabled here
 * that is not allowed there is a 400 on save, which a person reads as "it lost
 * what I wrote". Turning a node off is therefore the same decision made in two
 * files, and they have to be edited together.
 *
 * `codeBlock` is replaced with the lowlight version rather than added beside
 * it. They are the same node name, so registering both makes Tiptap's schema
 * throw on a duplicate — StarterKit's own is switched off and this one takes
 * its place, which keeps the document shape identical to what the server
 * already validates. The highlighting is decoration applied to the rendered
 * view; it changes nothing about what is stored.
 */
export function editorExtensions(options: { placeholderAriaLabel?: string } = {}): AnyExtension[] {
  void options;
  return [
    StarterKit.configure({
      // Only what message-body.ts allowlists — see that file's header for the
      // full node/mark reasoning.
      heading: false,
      blockquote: false,
      horizontalRule: false,
      strike: false,
      underline: false,
      // Replaced below. Left on, it collides with CodeBlockLowlight.
      codeBlock: false,
      link: {
        protocols: ['http', 'https'],
        defaultProtocol: 'https',
        autolink: true,
        // Writing must not navigate the tab away mid-sentence; the rendered
        // views (MessageBodyView) are where links open.
        openOnClick: false,
      },
    }),
    CodeBlockLowlight.configure({
      lowlight,
      // No language on a pasted block, so let it guess from the registered
      // grammars — see highlighting.ts for what is registered and why.
      defaultLanguage: null,
    }),
    MentionNode,
    ImageNode,
  ];
}
